import { customFieldMediaSrc, labelForCustomFieldValue, parseCustomFieldMediaIds } from '@/lib/custom-field-display';
import { formatChileanPeso } from '@/lib/format-chilean-peso';
import { formatCustomFieldDateDisplay } from '@/lib/format-custom-field-date';

export const PERSONAL_AND_GENERAL_GROUP = 'Información Personal y General';

export interface UserShowCustomField {
  id: string;
  name: string;
  slug: string;
  type: string;
  value: unknown;
  rawValue?: string | null;
  options?: Array<{ label: string; value: string }>;
  mediaFiles?: Array<{ id: string; title?: string | null; filename?: string | null }>;
}

function foldGroupName(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function isPersonalOrGeneralGroup(groupName: string) {
  const name = foldGroupName(groupName);
  return name.includes('informacion personal') || name === 'personal' || name === 'general';
}

export function isCustomFieldMediaType(type: string) {
  const kind = type.toLowerCase();
  return kind === 'image' || kind === 'file';
}

export function mediaFileDisplayName(file: { title?: string | null; filename?: string | null } | undefined, fallback = 'Imagen') {
  const title = file?.title?.trim();
  if (title) return title;
  const filename = file?.filename?.trim();
  if (filename) return filename;
  return fallback;
}

export function userShowFieldSections(
  personalFields: UserShowCustomField[],
  customSections: { groupName: string; fields: UserShowCustomField[] }[],
  phone?: string | null,
): { groupName: string; fields: UserShowCustomField[] }[] {
  const phoneField: UserShowCustomField = {
    id: '__user-phone',
    name: 'Teléfono',
    slug: 'phone',
    type: 'phone',
    value: phone?.trim() ? phone.trim() : null,
    rawValue: phone ?? null,
  };
  const personalAndGeneral: UserShowCustomField[] = [phoneField, ...personalFields];
  const rest: { groupName: string; fields: UserShowCustomField[] }[] = [];

  for (const section of customSections) {
    if (isPersonalOrGeneralGroup(section.groupName)) {
      personalAndGeneral.push(...section.fields);
    } else {
      rest.push(section);
    }
  }

  return [{ groupName: PERSONAL_AND_GENERAL_GROUP, fields: personalAndGeneral }, ...rest];
}

interface CustomFieldDisplayProps {
  type: string;
  value: unknown;
  rawValue?: string | null;
  options?: Array<{ label: string; value: string }>;
}

export function formatUserCustomFieldValue({ type, value, rawValue, options }: CustomFieldDisplayProps) {
  const normalized = type.toLowerCase();

  if (normalized === 'checkbox' || normalized === 'switch') {
    return value ? 'Sí' : 'No';
  }

  if (normalized === 'color' && typeof value === 'string' && value) {
    return (
      <span className="inline-flex items-center justify-end gap-2">
        <span
          className="inline-block h-4 w-4 rounded border border-dash-outline-variant"
          style={{ backgroundColor: value }}
          aria-hidden="true"
        />
        {value}
      </span>
    );
  }

  if (normalized === 'image' || normalized === 'file') {
    const ids = parseCustomFieldMediaIds(value ?? rawValue);
    if (ids.length === 0) return '—';
    if (normalized === 'image') {
      return (
        <span className="inline-flex flex-wrap justify-start gap-2">
          {ids.map((id) => {
            const src = customFieldMediaSrc(id);
            return src ? (
              <img
                key={id}
                src={src}
                alt=""
                className="h-16 w-16 rounded-lg border border-dash-outline-variant object-cover"
              />
            ) : null;
          })}
        </span>
      );
    }
    return (
      <span className="inline-flex flex-col items-start gap-1">
        {ids.map((id, index) => {
          const src = customFieldMediaSrc(id);
          return (
            <a
              key={id}
              href={src ?? id}
              className="font-medium text-dash-secondary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {ids.length > 1 ? `Archivo ${index + 1}` : 'Ver archivo'}
            </a>
          );
        })}
      </span>
    );
  }

  if (normalized === 'peso') {
    const formatted = formatChileanPeso(typeof value === 'string' && value.trim() ? value : rawValue);
    return formatted || '—';
  }

  const optionLabel = labelForCustomFieldValue(normalized, value ?? rawValue, options);
  if (optionLabel) return optionLabel;
  if ((normalized === 'url' || normalized === 'email') && typeof value === 'string' && value) {
    const href = normalized === 'email' ? `mailto:${value}` : value;
    return (
      <a href={href} className="font-medium text-dash-secondary hover:underline" target="_blank" rel="noopener noreferrer">
        {value}
      </a>
    );
  }

  const dateSource = typeof value === 'string' && value.trim() ? value : rawValue;
  if (dateSource) {
    const formattedDate = formatCustomFieldDateDisplay(normalized, dateSource);
    if (formattedDate) return formattedDate;
  }

  if (typeof value === 'string' && value.trim()) return value;
  if (rawValue?.trim()) return rawValue;
  return '—';
}

export function formatDateTimeEs(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTimeSecondsEs(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function relativeTimeEs(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = date.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  const minutes = Math.round(diffMs / 60000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  const days = Math.round(hours / 24);
  return rtf.format(days, 'day');
}

const ROLE_SHOW_ICONS: Record<string, string> = {
  'super-admin': 'admin_panel_settings',
  admin: 'admin_panel_settings',
  teacher: 'co_present',
  student: 'school',
};

export function userRoleIcon(role: string) {
  return ROLE_SHOW_ICONS[role] ?? 'badge';
}

export function userStatusLabel(status?: string) {
  if (status === 'inactive') return 'Inactivo';
  if (status === 'deleted') return 'Eliminado';
  return 'Activo';
}
